import { Star, MessageCircle } from "lucide-react";
import { WHATSAPP_URL } from "../lib/whatsapp";
import { PageHero } from "../components/PageSections";
import { useSeo } from "../lib/seo";
import { BookingProofSection } from "../components/BookingProofSection";

const drivingTestReviews = [
  {
    name: "Sarah M.",
    location: "Luton",
    text: "Really quick and straightforward from start to finish. I'd been checking for earlier tests for weeks and EDT managed to help me get one much sooner than I expected. Communication on WhatsApp was great throughout.",
  },
  {
    name: "Jayden K.",
    location: "Birmingham",
    text: "Paid on Monday and had a confirmed test date within a couple of days. Everything was explained clearly and I was kept updated the whole time. Would definitely recommend.",
  },
  {
    name: "Chloe R.",
    location: "Bedford",
    text: "Honestly made the whole process so much easier. I gave them the dates I could do and they sorted the rest. Really friendly service and quick replies.",
  },
  {
    name: "Mohammed A.",
    location: "Milton Keynes",
    text: "Very professional and easy to deal with. I needed an earlier test because of work and they managed to help me find a much better date. Excellent communication.",
  },
  {
    name: "Ellie T.",
    location: "Northampton",
    text: "I was struggling to find anything suitable myself. EDT kept me updated and found a test that worked around my availability. Really happy with the service.",
  },
];

const claimsReviews = [
  {
    name: "Daniel P.",
    location: "Luton",
    text: "After my accident I didn't really know what I needed to do. EDT explained everything properly and helped organise the claim and repair process. Made a stressful situation much easier.",
  },
  {
    name: "Liam H.",
    location: "Bedfordshire",
    text: "Very professional service. They dealt with everything quickly and kept me informed throughout. The car came back looking brilliant and I didn't have to keep chasing for updates.",
  },
  {
    name: "Rebecca S.",
    location: "Hertfordshire",
    text: "Really impressed with the communication and quality of the repair. They helped me understand the insurance side of things and handled everything from start to finish.",
  },
  {
    name: "Adam C.",
    location: "Milton Keynes",
    text: "Had bodywork damage repaired privately and the finish was spot on. Fair price, good communication and the car looked great when I collected it.",
  },
  {
    name: "Sophie L.",
    location: "Luton",
    text: "Great service after a non-fault accident. They were helpful from the first phone call and kept everything simple. Would happily use them again.",
  },
];

const ReviewGrid = ({ reviews, testIdPrefix }) => (
  <div className="mt-8 grid gap-px border border-[rgba(201,162,75,0.18)] bg-[rgba(201,162,75,0.18)] sm:grid-cols-2 lg:grid-cols-3">
    {reviews.map((t, i) => (
      <figure
        key={`${testIdPrefix}-${i}`}
        data-testid={`${testIdPrefix}-review-${i + 1}`}
        className="flex flex-col bg-[#111110] p-7"
      >
        <div className="flex gap-1 text-[#c9a24b]" aria-hidden="true">
          {[...Array(5)].map((_, s) => (
            <Star key={s} size={13} fill="currentColor" strokeWidth={0} />
          ))}
        </div>
        <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-[#a09a8c]">
          {t.text}
        </blockquote>
        <figcaption className="mt-5 border-t border-[rgba(201,162,75,0.15)] pt-4">
          <span className="text-sm font-semibold text-[#e6c87a]">{t.name}</span>
          <span className="mt-1 block text-xs uppercase tracking-[0.15em] text-[#6f6a5f]">
            {t.location}
          </span>
        </figcaption>
      </figure>
    ))}
  </div>
);

export default function Reviews() {
  useSeo(
    "EDT Solutions Reviews & Testimonials",
    "See what customers say about EDT's driving test booking support, insurance claim assistance and vehicle bodywork repairs across the UK."
  );
  return (
    <main data-testid="reviews-page">
      <PageHero
        label="Reviews & Testimonials"
        heading="What Our Customers Say"
        text="Feedback from people who have used EDT for driving test booking support, insurance claim assistance and bodywork repairs."
        testIdPrefix="reviews"
      />

      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="mt-2">
          <h2
            data-testid="driving-test-reviews-heading"
            className="font-display text-2xl text-[#f2ede3] sm:text-3xl"
          >
            Driving Test Reviews
          </h2>
          <ReviewGrid reviews={drivingTestReviews} testIdPrefix="driving-test" />
        </div>

        <div className="mt-16">
          <h2
            data-testid="claims-reviews-heading"
            className="font-display text-2xl text-[#f2ede3] sm:text-3xl"
          >
            Claims &amp; Accident Repair Reviews
          </h2>
          <ReviewGrid reviews={claimsReviews} testIdPrefix="claims" />
        </div>
      </section>

      {/* Driving Test Booking Proof */}
      <BookingProofSection testId="reviews-booking-proof" />

      <section className="border-t border-[rgba(201,162,75,0.22)] bg-[#111110]">
        <div className="mx-auto max-w-6xl px-5 py-12 text-center sm:px-8 sm:py-14">
          <div className="mx-auto h-px w-12 bg-[#c9a24b]" />
          <h2
            data-testid="reviews-cta-heading"
            className="font-display mx-auto mt-5 max-w-2xl text-2xl text-[#f2ede3] sm:text-3xl"
          >
            Ready to Get Started?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#a09a8c]">
            Message us on WhatsApp and tell us what you need — we'll take it
            from there.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-cta-whatsapp-button"
            className="mt-7 inline-flex items-center gap-2 bg-[#c9a24b] px-8 py-3.5 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a]"
          >
            <MessageCircle size={15} />
            Chat on WhatsApp
          </a>
        </div>
      </section>
    </main>
  );
}
